import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Activity, Mic, Send, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { getLeaderboards, type LeaderboardsResponse } from '../../services/annotationApi';
import { useCurrentUser } from '../../context/UserContext';
import { formatHm } from '../../utils/formatDuration';

type Window = '24h' | '7d' | '30d';

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function windowRange(w: Window): { from: string; to: string } {
  const now = new Date();
  const days = w === '24h' ? 1 : w === '7d' ? 7 : 30;
  const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  return { from: isoDate(from), to: isoDate(now) };
}


interface StatBoxProps {
  label: string;
  value: string;
  sub?: string;
  icon: React.ElementType;
  accentClass: string;
}

function StatBox({ label, value, sub, icon: Icon, accentClass }: StatBoxProps) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-500 font-medium">{label}</p>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accentClass}`}>
          <Icon size={18} className="text-white" />
        </div>
      </div>
      <p className="text-3xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </div>
  );
}

/// Pulls a single user's row off the (now self-scoped) leaderboards
/// response. Both `uploads.rows` and `transcribes.rows` will hold at most
/// one row when the caller is an enumerator or transcriber.
function extractMetrics(data: LeaderboardsResponse, isTranscriber: boolean) {
  if (isTranscriber) {
    const s = data.transcribes.summary;
    return {
      videoCount: s.submittedSessions,
      seconds: s.transcribedSeconds,
      approvedCount: s.approvedSessions,
      approvedSeconds: s.approvedSeconds,
    };
  }
  const s = data.uploads.summary;
  return {
    videoCount: s.totalSessions,
    seconds: s.recordingSeconds,
    approvedCount: 0,
    approvedSeconds: 0,
  };
}

export default function MyStatsPage() {
  const { role } = useCurrentUser();
  const [window, setWindow] = useState<Window>('24h');

  // All-time tally (no date filter).
  const allTime = useQuery({
    queryKey: ['leaderboards-self', 'all-time'],
    queryFn: () => getLeaderboards(),
    staleTime: 60_000,
  });

  // Rolling window (24h / 7d / 30d). React-Query caches per key so flipping
  // back and forth between toggles doesn't refetch unnecessarily.
  const windowed = useQuery({
    queryKey: ['leaderboards-self', window],
    queryFn: () => getLeaderboards(windowRange(window)),
    staleTime: 60_000,
  });

  const isTranscriber = role === 'transcriber';
  const isEnumerator = role === 'enumerator';

  if (!isTranscriber && !isEnumerator) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <AlertCircle className="w-10 h-10 text-gray-400" />
        <p className="text-gray-600 font-medium">This page is for transcribers and enumerators.</p>
      </div>
    );
  }

  const allTimeMetrics = allTime.data ? extractMetrics(allTime.data, isTranscriber) : null;
  const windowMetrics = windowed.data ? extractMetrics(windowed.data, isTranscriber) : null;

  const titleText = isTranscriber ? 'My Transcription Work' : 'My Recording Work';
  const subtitleText = isTranscriber
    ? 'Tasks you have submitted and what supervisors have approved.'
    : 'Sessions you have recorded across all projects.';

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
          <Activity className="w-5 h-5 text-violet-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{titleText}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{subtitleText}</p>
        </div>
      </div>

      {/* All-time section */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">All time</p>
        {allTime.isLoading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-8 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
          </div>
        ) : allTime.isError ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
            Could not load your stats.
          </div>
        ) : (
          <div className={`grid gap-4 ${isTranscriber ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' : 'grid-cols-1 sm:grid-cols-2'}`}>
            <StatBox
              label={isTranscriber ? 'Tasks submitted' : 'Videos recorded'}
              value={`${allTimeMetrics?.videoCount ?? 0}`}
              icon={isTranscriber ? Send : Mic}
              accentClass="bg-violet-500"
            />
            <StatBox
              label={isTranscriber ? 'Total transcribed' : 'Total recorded'}
              value={formatHm(allTimeMetrics?.seconds ?? 0)}
              icon={Activity}
              accentClass="bg-blue-500"
            />
            {isTranscriber && (
              <>
                <StatBox
                  label="Tasks approved"
                  value={`${allTimeMetrics?.approvedCount ?? 0}`}
                  icon={CheckCircle2}
                  accentClass="bg-emerald-500"
                />
                <StatBox
                  label="Approved duration"
                  value={formatHm(allTimeMetrics?.approvedSeconds ?? 0)}
                  icon={CheckCircle2}
                  accentClass="bg-emerald-600"
                />
              </>
            )}
          </div>
        )}
      </div>

      {/* Rolling window toggle */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Last…</p>
          <div className="inline-flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
            {(['24h', '7d', '30d'] as const).map((w) => (
              <button
                key={w}
                onClick={() => setWindow(w)}
                className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                  window === w
                    ? 'bg-violet-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {w}
              </button>
            ))}
          </div>
        </div>
        {windowed.isLoading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-8 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
          </div>
        ) : windowed.isError ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
            Could not load stats for this window.
          </div>
        ) : (
          <div className={`grid gap-4 ${isTranscriber ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' : 'grid-cols-1 sm:grid-cols-2'}`}>
            <StatBox
              label={isTranscriber ? 'Tasks submitted' : 'Videos recorded'}
              value={`${windowMetrics?.videoCount ?? 0}`}
              icon={isTranscriber ? Send : Mic}
              accentClass="bg-violet-500"
              sub={`in last ${window}`}
            />
            <StatBox
              label={isTranscriber ? 'Total transcribed' : 'Total recorded'}
              value={formatHm(windowMetrics?.seconds ?? 0)}
              icon={Activity}
              accentClass="bg-blue-500"
              sub={`in last ${window}`}
            />
            {isTranscriber && (
              <>
                <StatBox
                  label="Tasks approved"
                  value={`${windowMetrics?.approvedCount ?? 0}`}
                  icon={CheckCircle2}
                  accentClass="bg-emerald-500"
                  sub={`in last ${window}`}
                />
                <StatBox
                  label="Approved duration"
                  value={formatHm(windowMetrics?.approvedSeconds ?? 0)}
                  icon={CheckCircle2}
                  accentClass="bg-emerald-600"
                  sub={`in last ${window}`}
                />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
